import { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";
import { Tooltip } from "react-tooltip";

import { ChatMessage, ConversationContact } from "../../aiConversations.types";
import { isCorrectableMessage } from "../../helpers/correction";
import { ActionCard } from "../ActionCard";
import { AudioBubble } from "../AudioBubble";
import { CarouselBubble } from "../CarouselBubble";
import { ChatComposer } from "../ChatComposer";
import { ChatHeader } from "../ChatHeader";
import { ImageBubble } from "../ImageBubble";
import { MessageBubble } from "../MessageBubble";
import { VideoBubble } from "../VideoBubble";

import styles from "./ChatView.module.scss";

export type ChatToggleMode = "ai" | "human";
type ToggleMode = ChatToggleMode;

interface Props {
  contact: ConversationContact;
  messages: ChatMessage[];
  mode: ToggleMode;
  onModeChange: (next: ToggleMode) => void;
  toggleDisabled?: boolean;
  togglingMode?: boolean;
  onSend?: (payload: { text?: string; imageUrl?: string }) => void;
  sending?: boolean;
  composerDisabled?: boolean;
  composerDisabledReason?: string;
  onLoadOlder?: () => void;
  hasOlder?: boolean;
  isLoadingOlder?: boolean;
  isLoadingMessages?: boolean;
  aiTyping?: boolean;
  onCreateReturn?: () => void;
  // "Correct this reply" under the AI's own replies. Only the WhatsApp
  // conversations pass these; in-app support shows no button.
  canCorrect?: boolean;
  onCorrectMessage?: (message: ChatMessage) => void;
  // WhatsApp only: what the assistant heard in a voice note, by message id,
  // from the turn snapshots loaded so far.
  voiceTranscripts?: Record<string, string>;
}

const LOAD_OLDER_THRESHOLD_PX = 80;

const ChatView = ({
  contact,
  messages,
  mode,
  onModeChange,
  onCreateReturn,
  toggleDisabled,
  togglingMode,
  onSend,
  sending,
  composerDisabled,
  composerDisabledReason,
  onLoadOlder,
  hasOlder,
  isLoadingOlder,
  isLoadingMessages,
  aiTyping,
  canCorrect,
  onCorrectMessage,
  voiceTranscripts
}: Props) => {
  const { t } = useTranslation();
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const previousScrollHeightRef = useRef<number | null>(null);
  const previousMessageCountRef = useRef<number>(0);
  const previousTopIdRef = useRef<string | null>(null);
  const previousBottomIdRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const node = messagesRef.current;
    if (!node) return;

    const conversationChanged = conversationIdRef.current !== contact.id;
    conversationIdRef.current = contact.id;

    const previousCount = previousMessageCountRef.current;
    const currentCount = messages.length;

    const previousTopId = previousTopIdRef.current;
    const previousBottomId = previousBottomIdRef.current;

    const newTopId = messages[0]?.id ?? null;
    const newBottomId = messages[messages.length - 1]?.id ?? null;

    if (conversationChanged || previousCount === 0) {
      node.scrollTop = node.scrollHeight;
    } else if (
      previousTopId &&
      newTopId !== previousTopId &&
      previousScrollHeightRef.current !== null
    ) {
      const delta = node.scrollHeight - previousScrollHeightRef.current;
      node.scrollTop = node.scrollTop + delta;
    } else if (newBottomId !== previousBottomId) {
      const distanceFromBottom =
        node.scrollHeight - node.scrollTop - node.clientHeight;
      if (distanceFromBottom < 120) {
        node.scrollTop = node.scrollHeight;
      }
    }

    previousMessageCountRef.current = currentCount;
    previousTopIdRef.current = newTopId;
    previousBottomIdRef.current = newBottomId;
    previousScrollHeightRef.current = node.scrollHeight;
  }, [messages, contact.id]);

  useEffect(() => {
    const node = messagesRef.current;
    if (!node || !onLoadOlder) return;

    const handleScroll = () => {
      if (!hasOlder || isLoadingOlder) return;
      if (node.scrollTop <= LOAD_OLDER_THRESHOLD_PX) {
        previousScrollHeightRef.current = node.scrollHeight;
        onLoadOlder();
      }
    };

    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [onLoadOlder, hasOlder, isLoadingOlder]);

  const correctHandler = (message: ChatMessage) =>
    canCorrect && onCorrectMessage && isCorrectableMessage(message)
      ? () => onCorrectMessage(message)
      : undefined;

  return (
    <div className={styles.view}>
      <ChatHeader
        contact={contact}
        mode={mode}
        onModeChange={onModeChange}
        submitting={togglingMode}
        disabled={toggleDisabled}
        onCreateReturn={onCreateReturn}
      />

      <div className={styles.messages} ref={messagesRef}>
        {isLoadingMessages && messages.length === 0 ? (
          <div className={styles.loaderCentered}>
            <ClipLoader size={32} color="#fb3973" />
          </div>
        ) : (
          <>
            {isLoadingOlder ? (
              <div className={styles.loadingIndicator}>
                <ClipLoader size={18} color="#fb3973" />
              </div>
            ) : null}
            {messages.map((message) => {
              if (message.variant === "text") {
                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    incomingName={contact.name}
                    incomingAvatar={contact.avatarUrl}
                    onCorrect={correctHandler(message)}
                  />
                );
              }
              if (message.variant === "image") {
                return (
                  <ImageBubble
                    key={message.id}
                    message={message}
                    incomingName={contact.name}
                    incomingAvatar={contact.avatarUrl}
                    onCorrect={correctHandler(message)}
                  />
                );
              }
              if (message.variant === "video") {
                return (
                  <VideoBubble
                    key={message.id}
                    message={message}
                    incomingName={contact.name}
                    incomingAvatar={contact.avatarUrl}
                    onCorrect={correctHandler(message)}
                  />
                );
              }
              if (message.variant === "carousel") {
                return (
                  <CarouselBubble
                    key={message.id}
                    message={message}
                    incomingName={contact.name}
                    incomingAvatar={contact.avatarUrl}
                    onCorrect={correctHandler(message)}
                  />
                );
              }
              if (message.variant === "audio") {
                return (
                  <AudioBubble
                    key={message.id}
                    message={message}
                    incomingName={contact.name}
                    incomingAvatar={contact.avatarUrl}
                    transcript={voiceTranscripts?.[message.id]}
                  />
                );
              }
              return <ActionCard key={message.id} message={message} />;
            })}
            {aiTyping ? (
              <div className={styles.typingRow}>
                <div className={styles.typingBubble}>
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      <ChatComposer
        onSend={onSend}
        disabled={sending || composerDisabled}
        disabledReason={composerDisabled ? composerDisabledReason : undefined}
      />

      {/* One tooltip for every "unknown origin" label (see BubbleMeta). */}
      <Tooltip
        anchorSelect="[data-unknown-origin]"
        place="top"
        opacity={1}
        offset={6}
      >
        {t("aiTurn.unknownOrigin")}
      </Tooltip>
    </div>
  );
};

export default ChatView;
